import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import { toast } from '../lib/toast.js';
import StepWelcome from './onboarding/StepWelcome.js';
import StepBasics from './onboarding/StepBasics.js';
import StepLabels from './onboarding/StepLabels.js';
import StepStages from './onboarding/StepStages.js';
import StepInvite from './onboarding/StepInvite.js';
import StepDone from './onboarding/StepDone.js';
import type { OnboardingData } from './onboarding/types.js';
import type { User, Workspace } from '@mayksel/types';

const TOTAL_STEPS = 6;

const DEFAULT_STAGES_BY_INDUSTRY: Record<string, string[]> = {
  badges_credentials: ['Artwork Received', 'Art Approval', 'Prepress', 'Printing', 'Finishing', 'QC', 'Ready to Ship', 'Shipped'],
  print_signage: ['Quote', 'Art Approval', 'Prepress', 'Production', 'Finishing', 'QC', 'Ready', 'Shipped'],
  apparel_promotional: ['Order Received', 'Art Approval', 'Sourcing', 'Production', 'QC', 'Ready', 'Shipped'],
  default: ['New', 'In Progress', 'Review', 'Complete'],
};

export default function Onboarding() {
  const { t } = useTranslation('onboarding');
  const { getToken } = useAuth();
  const { user: clerkUser } = useUser();
  const navigate = useNavigate();
  const setWorkspaceData = useWorkspaceStore((s) => s.setWorkspaceData);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<OnboardingData>({
    companyName: '',
    industry: 'print_signage',
    bucketLabel: 'Project',
    bucketLabelPlural: 'Projects',
    orderLabel: 'Order',
    orderLabelPlural: 'Orders',
    stages: [],
    invites: [],
  });

  function updateData(patch: Partial<OnboardingData>) {
    setData((prev) => ({ ...prev, ...patch }));
  }

  function handleIndustryChange(industry: OnboardingData['industry']) {
    const defaultNames = DEFAULT_STAGES_BY_INDUSTRY[industry] ?? ['New', 'In Progress', 'Review', 'Complete'];
    const stages = defaultNames.map((name) => ({ name }));
    updateData({ industry, stages });
  }

  async function handleComplete() {
    setSubmitting(true);
    try {
      const token = await getToken();
      const result = await api.post<{ workspace: Workspace; user: User }>(
        '/v1/workspaces',
        {
          name: data.companyName,
          industry: data.industry,
          bucketLabel: data.bucketLabel,
          bucketLabelPlural: data.bucketLabelPlural,
          orderLabel: data.orderLabel,
          orderLabelPlural: data.orderLabelPlural,
          stages: data.stages,
          email: clerkUser?.primaryEmailAddress?.emailAddress ?? '',
          ownerName: clerkUser?.fullName ?? clerkUser?.primaryEmailAddress?.emailAddress ?? '',
        },
        { token: token ?? undefined },
      );

      setWorkspaceData(result.workspace, result.user);
      setStep(6);
    } catch (_err) {
      toast.error(t('done.error', 'Failed to set up workspace. Please try again.'));
      setSubmitting(false);
    }
  }

  function handleGoToDashboard() {
    navigate('/dashboard');
  }

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'var(--color-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
      }}
    >
      {/* Progress bar */}
      {step > 1 && step < 6 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'var(--color-gray-100)',
          }}
        >
          <div
            style={{
              height: '100%',
              background: 'var(--color-brand)',
              width: `${((step - 1) / (TOTAL_STEPS - 1)) * 100}%`,
              transition: 'width var(--duration-standard) var(--ease-out)',
            }}
          />
        </div>
      )}

      {/* Step indicator */}
      {step > 1 && step < 6 && (
        <p
          style={{
            position: 'fixed',
            top: 'var(--space-4)',
            right: 'var(--space-6)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-muted)',
          }}
        >
          {t('progress', { current: step - 1, total: TOTAL_STEPS - 2 })}
        </p>
      )}

      <div style={{ width: '100%', maxWidth: 520 }}>
        {step === 1 && (
          <StepWelcome onNext={() => setStep(2)} />
        )}
        {step === 2 && (
          <StepBasics
            data={data}
            onChange={updateData}
            onIndustryChange={handleIndustryChange}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <StepLabels
            data={data}
            onChange={updateData}
            onBack={() => setStep(2)}
            onNext={() => setStep(4)}
          />
        )}
        {step === 4 && (
          <StepStages
            data={data}
            onChange={updateData}
            onBack={() => setStep(3)}
            onNext={() => setStep(5)}
          />
        )}
        {step === 5 && (
          <StepInvite
            data={data}
            onChange={updateData}
            onBack={() => setStep(4)}
            onNext={handleComplete}
            submitting={submitting}
          />
        )}
        {step === 6 && (
          <StepDone
            data={data}
            onDone={handleGoToDashboard}
          />
        )}
      </div>
    </div>
  );
}
